import { nameByPlatform } from "~/constants/platfroms";
import {
  CautionReason,
  type CautionReasonValue,
  type IMarket,
  WithheldReason,
  type WithheldReasonValue,
} from "~/types/event";
import { formatCents } from "~/utils/formatPrice";
import { formatRowMoney } from "~/utils/formatRowMoney";
import { isFiniteNumber } from "~/utils/isFiniteNumber";

import {
  EmptySide,
  type IMarketQualityHover,
  SpreadBand,
} from "./marketQualityTypes";

type MarketQuality = NonNullable<IMarket["quality"]>;

export const NO_SPREAD = "—";

export const labelBySpreadBand: Record<SpreadBand, string> = {
  [SpreadBand.TIGHT]: "Tight",
  [SpreadBand.MODERATE]: "Moderate",
  [SpreadBand.WIDE]: "Wide",
  [SpreadBand.VERY_WIDE]: "Very wide",
};

export const labelByEmptySide: Record<EmptySide, string> = {
  [EmptySide.NO_BIDS]: "No bids",
  [EmptySide.NO_ASKS]: "No asks",
  [EmptySide.NO_ORDERS]: "No orders",
};

interface IReasonCopy {
  why: string;
  evidence: string;
}

interface IReasonContext {
  market: IMarket;
  name: string;
  spread: number;
}

enum WithheldEffect {
  REMOVED_FROM_BEST_ORDERS = "removed-from-best-orders",
  STILL_IN_BEST_ORDERS = "still-in-best-orders",
}

const WITHHELD_TITLE = "Price withheld";
const CAUTION_TITLE = "Use with caution";
const CAUTION_EFFECT =
  "Prices shown and still counted in PH Consensus. A limit order lets you set the most you'll pay.";

const effectByWithheldReason: Record<WithheldReasonValue, WithheldEffect> = {
  [WithheldReason.UNTRADED]: WithheldEffect.STILL_IN_BEST_ORDERS,
  [WithheldReason.IMPOSSIBLE_PRICE]: WithheldEffect.REMOVED_FROM_BEST_ORDERS,
  [WithheldReason.CROSSED]: WithheldEffect.REMOVED_FROM_BEST_ORDERS,
  [WithheldReason.NO_ORDERS]: WithheldEffect.REMOVED_FROM_BEST_ORDERS,
  [WithheldReason.WIDE_NO_TRADE_PRICE]: WithheldEffect.STILL_IN_BEST_ORDERS,
  [WithheldReason.OUT_OF_LINE]: WithheldEffect.STILL_IN_BEST_ORDERS,
};

const effectTextByWithheldEffect: Record<
  WithheldEffect,
  (name: string) => string
> = {
  [WithheldEffect.REMOVED_FROM_BEST_ORDERS]: (name) =>
    `${name}'s prices for this market look unreliable, so we've removed them from PH Consensus, Best buy and Best sell. If you still trade there, check ${name}'s live order book first — your order may fill at a very different price.`,
  [WithheldEffect.STILL_IN_BEST_ORDERS]: (name) =>
    `${name}'s price for this market looks unreliable, so we've left it out of PH Consensus. Its orders still count for Best buy and Best sell. If you trade there, check ${name}'s live order book first — your order may fill at a very different price.`,
};

const percent = (chance: number): string => `${Math.round(chance * 100)}%`;

const sentenceCase = (text: string): string =>
  text.charAt(0).toUpperCase() + text.slice(1);

const describeOrders = ({ ownBid, ownAsk }: IMarket): string => {
  const buy = isFiniteNumber(ownAsk) ? `buy ${formatCents(ownAsk)}` : "no asks";
  const sell = isFiniteNumber(ownBid)
    ? `sell ${formatCents(ownBid)}`
    : "no bids";

  return `${buy} · ${sell}`;
};

const describeOrdersAt = ({ ownBid, ownAsk }: IMarket): string => {
  const buy = isFiniteNumber(ownAsk)
    ? `Buy at ${formatCents(ownAsk)}`
    : "No asks";
  const sell = isFiniteNumber(ownBid)
    ? `sell at ${formatCents(ownBid)}`
    : "no bids";

  return `${buy}, ${sell}`;
};

const withTraded = ({ volume }: IMarket, evidence: string): string =>
  volume ? `${evidence} · ${formatRowMoney(volume)} traded` : evidence;

const describeShownChance = ({ chance }: IMarket): string =>
  isFiniteNumber(chance) ? `The ${percent(chance)} shown` : "The price shown";

const describeMidpoint = ({ chance }: IMarket): string =>
  isFiniteNumber(chance)
    ? `The ${percent(chance)} in between`
    : "The price in between";

const describeOwnOrders = ({ ownBid, ownAsk }: IMarket): string => {
  if (isFiniteNumber(ownBid) && isFiniteNumber(ownAsk)) {
    return `its own orders sit at buy ${formatCents(ownAsk)} · sell ${formatCents(ownBid)}`;
  }

  if (isFiniteNumber(ownAsk)) {
    return `nobody sells below ${formatCents(ownAsk)} there`;
  }

  return isFiniteNumber(ownBid)
    ? `nobody buys above ${formatCents(ownBid)} there`
    : "it has no orders";
};

const reasonCopyByWithheldReason: Record<
  WithheldReasonValue,
  (context: IReasonContext) => IReasonCopy
> = {
  [WithheldReason.UNTRADED]: ({ market }) => ({
    why: "Nobody has traded this market yet, so there is no price to trust: the orders are real but untested.",
    evidence: `${sentenceCase(describeOrders(market))} · no trade on record`,
  }),
  [WithheldReason.IMPOSSIBLE_PRICE]: ({ market }) => ({
    why: `One side is quoted at ${market.ownAsk === 0 ? "0¢" : "100¢"}. Nobody trades a contract for nothing, so the order book is empty or broken.`,
    evidence: sentenceCase(describeOrders(market)),
  }),
  [WithheldReason.CROSSED]: ({ market }) => ({
    why: "The buy and sell prices are the wrong way round: someone is offering to buy for more than the cheapest seller asks. That can't happen in a working market, so this is almost certainly a data error.",
    evidence: sentenceCase(describeOrders(market)),
  }),
  [WithheldReason.NO_ORDERS]: ({ market }) => ({
    why: `Nobody is buying or selling this market right now, so there is nothing to price it against.${isFiniteNumber(market.chance) ? ` ${describeShownChance(market)} is the last trade.` : ""}`,
    evidence: withTraded(market, "No buy or sell orders"),
  }),
  [WithheldReason.WIDE_NO_TRADE_PRICE]: ({ market, spread }) => ({
    why: `${describeOrdersAt(market)}: a gap of ${formatCents(spread, { forceFixed1: true })}. ${describeMidpoint(market)} is just the midpoint, not a price anyone traded at, so we don't show it as a chance. The gap is also what it would cost you to get in and back out (slippage).`,
    evidence: withTraded(market, sentenceCase(describeOrders(market))),
  }),
  [WithheldReason.OUT_OF_LINE]: ({ market, name }) => ({
    why: `${name} shows ${isFiniteNumber(market.chance) ? percent(market.chance) : "a price"}, but ${describeOwnOrders(market)}. When the two disagree by more than 10 points, we can't tell which is right.`,
    evidence: `Shown ${isFiniteNumber(market.chance) ? percent(market.chance) : "—"} · ${describeOrders(market)}`,
  }),
};

const cautionTextByCautionReason: Record<
  CautionReasonValue,
  (context: IReasonContext) => string
> = {
  [CautionReason.NO_BIDS]: ({ market, name }) =>
    `No bids. Nobody is offering to buy on ${name} right now, so the price can only be checked against the ${formatCents(market.ownAsk)} ask.`,
  [CautionReason.NO_ASKS]: ({ market, name }) =>
    `No asks. Nobody is offering to sell on ${name} right now, so the price can only be checked against the ${formatCents(market.ownBid)} bid.`,
  [CautionReason.VERY_WIDE_SPREAD]: ({ market, spread }) =>
    `Very wide spread. ${describeOrdersAt(market)}: a gap of ${formatCents(spread, { forceFixed1: true })}. That gap is what it costs you to get in and back out, and bigger orders can fill even further away (slippage).`,
};

export const getCautionHover = (
  market: IMarket,
  { cautionReasons, spread }: MarketQuality
): IMarketQualityHover => {
  const name = nameByPlatform[market.platform];

  return {
    title: CAUTION_TITLE,
    text: [
      ...cautionReasons.map((reason) =>
        cautionTextByCautionReason[reason]({ market, name, spread })
      ),
      withTraded(market, sentenceCase(describeOrders(market))),
      CAUTION_EFFECT,
    ].join("\n\n"),
  };
};

export const getWithheldHover = (
  market: IMarket,
  { withheldReason, spread }: MarketQuality
): IMarketQualityHover => {
  const name = nameByPlatform[market.platform];
  const effect = withheldReason
    ? effectByWithheldReason[withheldReason]
    : WithheldEffect.REMOVED_FROM_BEST_ORDERS;
  const reason = withheldReason
    ? reasonCopyByWithheldReason[withheldReason]({ market, name, spread })
    : null;

  return {
    title: WITHHELD_TITLE,
    text: [
      ...(reason ? [reason.why, reason.evidence] : []),
      effectTextByWithheldEffect[effect](name),
    ].join("\n\n"),
  };
};
